import React, { useState, useCallback, useEffect } from 'react';

import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import ExpandedRadioButton from 'Shared/components/ExpandedRadioButton/ExpandedRadioButton';
import { exportGoals } from 'Shared/api/Goals.api';
import { useToggle } from 'Shared/utils/hooks';
import { showNotificationError, downloadFile } from 'Shared/helpers';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';

import ExportGoalDatePicker from '../ExportGoalDatePicker/ExportGoalDatePicker';

import './ExportGoalsModal.scss';

type Props = {
  onToggle(): void;
};

const options = {
  ALL: 'all',
  COMPLETED: 'completed',
  OPENED: 'opened',
};

const dateRangeDefaultValue = { startRange: null, endRange: null };

function ExportGoalsModal({ onToggle }: Props) {
  const [choosenOption, setChoosenOption] = useState(null);
  const [dateRange, setDateRange] = useState(dateRangeDefaultValue);
  const [isLoading, toggleIsLoading] = useToggle(false);

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Export Goals' });
  }, []);

  const handleRadioButtonClick = useCallback(option => {
    setChoosenOption(option);
    setDateRange(dateRangeDefaultValue);
  }, []);

  const handleCancelClick = () => onToggle();

  const handleExportClick = () => {
    toggleIsLoading();
    exportGoals({
      type: choosenOption,
      startRange: dateRange.startRange?.unix(),
      endRange: dateRange.endRange?.unix(),
    })
      .then(response => {
        const file = new Blob([response.data.text], { type: 'text/plain' });
        downloadFile({ file, filename: response.data.filename });
      })
      .catch(err => {
        showNotificationError(err);
      })
      .finally(() => toggleIsLoading());
  };

  const handleDateRangeChange = useCallback(value => setDateRange(value), []);

  const isSubmitDisabled =
    !choosenOption ||
    (choosenOption === options.ALL && (!dateRange.startRange || !dateRange.endRange)) ||
    (choosenOption === options.COMPLETED && (!dateRange.startRange || !dateRange.endRange));

  return (
    <WpModal size="sm" isOpen onToggle={onToggle}>
      <WpModalHeader>Export Goals</WpModalHeader>
      <WpModalBody>
        <div className="mb-4">Export goals to a .txt to quickly share progress reports.</div>
        <div className="ExportGoalsModal__group mb-2">What goals would you like to export?</div>
        <ExpandedRadioButton
          label="All open goals"
          option={options.OPENED}
          isSelected={choosenOption === options.OPENED}
          onClick={handleRadioButtonClick}
        />
        <ExpandedRadioButton
          label="All open goals and complete goals within a time frame"
          option={options.ALL}
          isSelected={choosenOption === options.ALL}
          onClick={handleRadioButtonClick}
        >
          <ExportGoalDatePicker dateRange={dateRange} onRangeChange={handleDateRangeChange} />
        </ExpandedRadioButton>
        <ExpandedRadioButton
          label="Complete goals within a time frame"
          option={options.COMPLETED}
          isSelected={choosenOption === options.COMPLETED}
          onClick={handleRadioButtonClick}
        >
          <ExportGoalDatePicker dateRange={dateRange} onRangeChange={handleDateRangeChange} />
        </ExpandedRadioButton>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={handleCancelClick}>
            Cancel
          </WpButton>
          <WpButton disabled={isSubmitDisabled} onClick={handleExportClick} loading={isLoading}>
            Export
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default ExportGoalsModal;
